import { FC, ReactNode, useEffect, useRef } from "react";

import { useMultiStyleConfig, Box } from "@chakra-ui/react";
import { Formik, FormikHelpers } from "formik";

import Loader from "@components/Loader";
import { useRouter } from "@lib/router";
import useAuthData from "@modules/auth/hooks/useAuthData";
import {
  gtmEcommercePusher,
  gtmEventEcommerceData,
  STEPS,
  EVENTS,
  isLvEnvironment,
  pushAddShippingInfo,
  pushAddToCart,
  pushViewItem,
} from "@modules/gtm";
import mapOrderValuesToGTMValues from "@modules/gtm/mapOrderValuesToGTMValues";
import { ROUTES } from "@utils/constants";

import { DeliveryMethod, OrderFields, OrderStep, SHIPMENT_STEP_DELIVERY_METHODS } from "../constants";
import useOrderData from "../context/useOrderData";
import useOrderPayment from "../hooks/useOrderPayment";
import { orderFormSchema, TOrderFormData } from "../models/orderForm";
import PackageInfo from "./packageInfo/PackageInfo";
import Receiver from "./receiver/Receiver";
import Sender from "./sender/Sender";
import Shipment from "./shipment/Shipment";
import Summary from "./summary/Summary";

type TOrderPageContentProps = {
  currentStep: OrderStep;
};

const stepComponentsMap = {
  [OrderStep.PackageInfo]: PackageInfo,
  [OrderStep.ShipmentDetails]: Shipment,
  [OrderStep.SenderDetails]: Sender,
  [OrderStep.ReceiverDetails]: Receiver,
  [OrderStep.Summary]: Summary,
};

const getPageContent = (step: OrderStep): ReactNode => {
  const StepComponent = stepComponentsMap[step];
  return StepComponent ? <StepComponent /> : null;
};

const OrderPageContent: FC<TOrderPageContentProps> = ({ currentStep }) => {
  const styles = useMultiStyleConfig("Order", {});

  const {
    initialValues,
    isShipmentSaveLoading,
    saveShipment,
    isShipmentEditLoading,
    isLoadingAddresses,
    dynamicData: { packageSizes },
    staticData: { paymentMethods },
    getGeoroutingErrors,
  } = useOrderData();

  const router = useRouter();

  const [authData] = useAuthData();
  const userData = { email: authData?.email, phone: authData?.phone };

  const hasFiredMountEvent = useRef(false);
  useEffect(() => {
    if (hasFiredMountEvent.current) {
      return;
    }

    if (currentStep === OrderStep.PackageInfo) {
      pushViewItem(userData);
      hasFiredMountEvent.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const { isLoading: isPaymentLoading, executeOrderPayment } = useOrderPayment();

  const pushLegacyCheckoutStep = (values: TOrderFormData, step: STEPS) => {
    if (isLvEnvironment()) {
      return;
    }
    gtmEcommercePusher(
      gtmEventEcommerceData(mapOrderValuesToGTMValues(values, packageSizes, paymentMethods), step, EVENTS.CHECKOUT)
    );
  };

  const handleSubmit = async (values: TOrderFormData, { setFieldValue }: FormikHelpers<TOrderFormData>) => {
    const georoutingErrors = getGeoroutingErrors();
    if (Object.keys(georoutingErrors).length > 0) {
      return;
    }

    const deliveryMethod = values[OrderFields.DELIVERY_METHOD] as DeliveryMethod;

    if (currentStep === OrderStep.PackageInfo) {
      pushLegacyCheckoutStep(values, STEPS.ONE);
      pushAddToCart(values, userData);
      router.push(
        SHIPMENT_STEP_DELIVERY_METHODS.includes(deliveryMethod) ? ROUTES.ORDER_SHIPMENT : ROUTES.ORDER_SENDER
      );
      return;
    }

    if (currentStep === OrderStep.SenderDetails) {
      pushLegacyCheckoutStep(values, STEPS.TWO);

      router.push(ROUTES.ORDER_RECEIVER);
      return;
    }

    if (currentStep === OrderStep.ReceiverDetails || currentStep === OrderStep.ShipmentDetails) {
      if (currentStep === OrderStep.ShipmentDetails) {
        pushLegacyCheckoutStep(values, STEPS.TWO);
      }
      pushLegacyCheckoutStep(values, STEPS.THREE);
      pushAddShippingInfo(values, userData);
      router.push(ROUTES.ORDER_SUMMARY);
      return;
    }

    if (currentStep === OrderStep.Summary) {
      pushLegacyCheckoutStep(values, STEPS.FOUR);
      const { isSuccess: isSaveSuccess, shipmentId } = await saveShipment(values);
      if (!isSaveSuccess) {
        return;
      }
      setFieldValue(OrderFields.SHIPMENT_ID, shipmentId);

      const paymentMethod = values[OrderFields.PAYMENT_METHOD];
      executeOrderPayment(shipmentId!, paymentMethod!);
    }
  };

  return (
    <Box sx={styles.pageContent}>
      <Formik
        initialValues={initialValues as any}
        validateOnChange={false}
        validateOnBlur
        validationSchema={orderFormSchema}
        onSubmit={handleSubmit}
      >
        {() => (
          <Loader
            sx={{ height: "100%" }}
            isLoading={isShipmentEditLoading || isShipmentSaveLoading || isPaymentLoading || isLoadingAddresses}
          >
            {getPageContent(currentStep)}
          </Loader>
        )}
      </Formik>
    </Box>
  );
};
export default OrderPageContent;
